import { Transition, Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import clsx, { ClassValue } from 'clsx';
import React, { Fragment, useCallback, useState } from 'react';

export interface ModalProps {
	title?: string | React.ReactNode;
	description?: string;
	className?: ClassValue;
	classNameMainContainer?: string;
	classNameTitle?: ClassValue;
	classNameCardContainer?: ClassValue;
	classNameContainerChildren?: ClassValue;
	noMaxHeight?: boolean;
	maxHeight?: string;
	children?: React.ReactNode;
	withXButton?: boolean;
	avoidClose?: boolean;
	onClose?: () => void;
	/** When set, the close icon calls this instead of dismissing the modal. */
	onRequestClose?: () => void;
	backgroundColor?: 'white' | 'linear';
	transparentBackground?: boolean;
	transitionClassName?: string;
	closeIconTopPosition?: string;
	footer?: React.ReactNode;
	classNameFooter?: ClassValue;
	viewportHeight?: number | null;
	isIOS?: boolean;
	scrollContainerRef?: any;
	applyHeaderStyle?: boolean;
}

/**
 * Custom hook to return a modal with its handler functions,
 * the returned modal has a default layout
 *
 * @returns
 * 		Modal: Default Modal element that will be rendered
 * 		isOpen: usual modal prop that says when the modal should appear
 * 		show: Handler function to show the modal
 * 		hide: Handler function to hide the modal
 */

const useModal = (): {
	Modal: React.FC<ModalProps>;
	isOpen: boolean;
	show: () => void;
	hide: () => void;
	toggle: () => void;
} => {
	const cancelButtonRef = React.useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	const show = (): void => {
		setIsOpen(true);
	};

	const hide = (): void => {
		setIsOpen(false);
	};

	const toggle = (): void => {
		setIsOpen(!isOpen);
	};

	const Modal: React.FC<ModalProps> = useCallback(
		(props) => (
			<Transition.Root appear show={isOpen} as={Fragment}>
				<Dialog
					as="div"
					className="relative"
					onClose={() => {
						if (!props.avoidClose) {
							hide();
							props.onClose?.();
						}
					}}
					static
					initialFocus={cancelButtonRef}
				>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div
							className={clsx('fixed inset-0 z-20', props.transitionClassName)}
							style={{
								background: props.transparentBackground
									? 'transparent'
									: 'rgba(71, 71, 71, 0.8)',
							}}
							onClick={() => {
								if (!props.avoidClose) {
									hide();
									props.onClose?.();
								}
							}}
						/>
					</Transition.Child>

					<div
						ref={props.scrollContainerRef}
						className={clsx(
							'fixed inset-0 z-30 overflow-y-auto',
							props.className
						)}
					>
						<div
							className={clsx(
								'flex h-full items-center justify-center p-4 text-center relative',
								props.classNameMainContainer
							)}
						>
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								{props.applyHeaderStyle ? (
									<Dialog.Panel
										className={clsx(
											'transform transition-all',
											'rounded-[32px] w-full md:w-auto py-6 lg:py-8 !pb-4 flex flex-col',
											props.classNameCardContainer
										)}
										style={{
											background:
												props.backgroundColor === 'white'
													? 'white'
													: 'linear-gradient(180deg, #FFFFFF 0%, #F4F4F9 100%)',
											boxShadow: '0px 14px 42px 0px rgba(8, 15, 52, 0.06)',
										}}
										onClick={(e) => e.stopPropagation()}
									>
										<div className="px-4 lg:px-8 border-b border-[#0D061614]">
											{props.title && (
												<Dialog.Title
													as="h3"
													className={clsx(
														'text-xl font-semibold text-black leading-6 mb-6',
														{ 'pr-8 lg:pr-2': props.withXButton },
														{ '!mb-0': !!props.description },
														props.classNameTitle
													)}
												>
													{props.title}
												</Dialog.Title>
											)}
											{props.description && (
												<Dialog.Description
													className={clsx(
														'text-sm text-[#6E6A73] text-left mt-1 !mb-4'
													)}
												>
													{props.description}
												</Dialog.Description>
											)}
											{props.withXButton && (
												<button
													type="button"
													aria-label="Close"
													className="absolute right-8 lg:right-10 outline-none z-50 rounded focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
													onClick={() => {
														if (props.onRequestClose) {
															props.onRequestClose();
															return;
														}
														hide();
														props.onClose && props.onClose();
													}}
													style={{
														top: props.closeIconTopPosition
															? props.closeIconTopPosition
															: '32px',
													}}
												>
													<XMarkIcon className="text-black w-6 h-6" />
												</button>
											)}
										</div>
										<div className="px-4 lg:px-8 flex-1 min-h-0">
											<div
												className={clsx(
													'h-full overflow-y-auto scroll-custom lg:px-2',
													props.classNameContainerChildren
												)}
												style={{
													maxHeight: props.noMaxHeight
														? undefined
														: props.maxHeight ?? '80vh',
												}}
											>
												{props.children}
											</div>
										</div>
										{props.footer && (
											<div className="px-4 lg:px-8 pt-4 shrink-0 border-t border-[#0D061614]">
												<div
													className={clsx(
														'pb-[env(safe-area-inset-bottom)]',
														props.classNameFooter
													)}
												>
													{props.footer}
												</div>
											</div>
										)}
									</Dialog.Panel>
								) : (
									<Dialog.Panel
										className={clsx(
											'transform transition-all',
											'rounded-[32px] w-full md:w-auto py-6 lg:py-8 px-4 lg:px-8',
											props.classNameCardContainer
										)}
										style={{
											background:
												props.backgroundColor === 'white'
													? 'white'
													: 'linear-gradient(180deg, #FFFFFF 0%, #F4F4F9 100%)',
											boxShadow: '0px 14px 42px 0px rgba(8, 15, 52, 0.06)',
										}}
										onClick={(e) => e.stopPropagation()}
									>
										{props.title && (
											<Dialog.Title
												as="h3"
												className={clsx(
													'text-xl font-semibold text-black leading-6 mb-6',
													{ 'pr-12 lg:pr-2': props.withXButton },
													{ '!mb-0': !!props.description },
													props.classNameTitle
												)}
											>
												{props.title}
											</Dialog.Title>
										)}
										{props.description && (
											<Dialog.Description
												className={clsx(
													'text-sm text-[#6E6A73] text-left mb-6 mt-1'
												)}
											>
												{props.description}
											</Dialog.Description>
										)}
										{props.withXButton && (
											<button
												type="button"
												aria-label="Close"
												className="absolute right-8 lg:right-10 outline-none z-50 rounded focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
												onClick={() => {
													if (props.onRequestClose) {
														props.onRequestClose();
														return;
													}
													hide();
													props.onClose && props.onClose();
												}}
												style={{
													top: props.closeIconTopPosition
														? props.closeIconTopPosition
														: '32px',
												}}
											>
												<XMarkIcon className="text-black w-6 h-6" />
											</button>
										)}

										<div
											className={clsx(
												'flex-1 overflow-y-auto scroll-custom lg:px-2',
												props.classNameContainerChildren
											)}
											style={{
												maxHeight: props.noMaxHeight
													? undefined
													: props.maxHeight ?? '80vh',
											}}
										>
											{props.children}
										</div>

										{props.footer && (
											<div
												className={clsx(
													'pt-4 pb-[env(safe-area-inset-bottom)]',
													props.classNameFooter
												)}
											>
												{props.footer}
											</div>
										)}
									</Dialog.Panel>
								)}
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition.Root>
		),
		[isOpen]
	);

	return { Modal, isOpen, show, hide, toggle };
};

export default useModal;
